/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useEffect, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { snackNotifications } from '../../notification/SnackBarUtils';
import { createRandomAlphaNumericId } from '../../utils';
import styles from './ReactQuillEditor.module.scss';
import { DigitalAssistantFeedbackTypes } from '../DocumentVersionFeedback/FeedbackType';
import { signatureHash } from './react-quill-utils';

type FeedbackAttachment = {
  filename: string;
  mimetype: string;
  bytesize: number;
  signature: string;
};

type ReactQuillEditorProps = {
  documentId: string | undefined;
  value: string;
  onChange: (html: string) => void;
  feedbackType: DigitalAssistantFeedbackTypes;
  disabled?: boolean;
  minHeight?: number | string;
  maxAttachments?: number;
  currentAttachmentCount?: number;
  onAttachmentAdded?: (att: FeedbackAttachment, file: File, uploadId: string) => boolean | void;
  hideToolbar?: boolean;
};

export default function ReactQuillEditor({
  value,
  onChange,
  disabled,
  minHeight = 210,
  onAttachmentAdded,
  maxAttachments,
  currentAttachmentCount,
  hideToolbar,
}: ReactQuillEditorProps) {
  const quillRef = useRef<ReactQuill | null>(null);
  const getQuill = () => quillRef.current?.getEditor?.() || null;
  const MAX_SIZE = 2 * 1024 * 1024;

  const syncHtml = () => {
    const quill = getQuill();
    if (!quill) return;
    onChange(quill.root?.innerHTML ?? '');
  };
  const isLimitReached =
    maxAttachments !== undefined && currentAttachmentCount !== undefined && currentAttachmentCount >= maxAttachments;
  const blockIfLimitReached = () => {
    if (isLimitReached) {
      snackNotifications.error('Only 2 attachments are allowed.');
      return true;
    }
    return false;
  };

  const insertImagePreview = (src: string, uploadId: string) => {
    const quill = getQuill();
    if (!quill) return;

    const range = quill.getSelection(true) || {
      index: quill.getLength(),
      length: 0,
    };

    quill.insertEmbed(range.index, 'image', src, 'user');
    requestAnimationFrame(() => {
      quill.setSelection(range.index + 1, 0, 'silent');
      quill.focus();
    });
    const imgs = Array.from(quill.root.querySelectorAll('img'));
    const inserted = imgs[imgs.length - 1] as HTMLImageElement | undefined;
    if (inserted) inserted.setAttribute('data-upload-id', uploadId);
    syncHtml();
  };

  const removeImageByUploadId = (uploadId: string) => {
    const quill = getQuill();
    if (!quill) return;
    const img = quill.root.querySelector(`img[data-upload-id="${uploadId}"]`) as HTMLImageElement | null;
    if (!img) return;
    img.remove();
    syncHtml();
  };

  const addAttachmentOnly = async (file: File, uploadId: string) => {
    try {
      if (file.size > MAX_SIZE) {
        snackNotifications.error('Sorry, Image size exceeds 2MB. Please try uploading a different image');
        removeImageByUploadId(uploadId);
        return;
      }
      const signature = await signatureHash(`${file.name}-${file.size}-${file.type}`);

      const ok = onAttachmentAdded?.(
        {
          filename: file.name,
          mimetype: file.type,
          bytesize: file.size,
          signature,
        },
        file,
        uploadId
      );

      if (ok === false) {
        removeImageByUploadId(uploadId);
      }
    } catch {
      snackNotifications.error('Unable to attach image');
      removeImageByUploadId(uploadId);
    }
  };

  const handleToolbarImage = () => {
    if (blockIfLimitReached()) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const uploadId = `toolbar-${createRandomAlphaNumericId()}`;

      const reader = new FileReader();
      reader.onload = async () => {
        const preview = reader.result as string;
        if (!preview) return;
        insertImagePreview(preview, uploadId);
        await addAttachmentOnly(file, uploadId);
      };
      reader.readAsDataURL(file);
    };
  };

  useEffect(() => {
    const quill = getQuill();
    if (!quill) return;

    const root = quill.root;

    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageItem = Array.from(items).find((it) => it.type?.startsWith('image/'));
      if (!imageItem) return;
      if (blockIfLimitReached()) return;
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (!file) return;

      const uploadId = `paste-${createRandomAlphaNumericId()}`;

      const reader = new FileReader();
      reader.onload = async () => {
        const preview = reader.result as string;
        if (!preview) return;

        insertImagePreview(preview, uploadId);
        await addAttachmentOnly(file, uploadId);
      };

      reader.readAsDataURL(file);
    };

    const prev = root.onpaste;
    root.onpaste = onPaste;

    return () => {
      root.onpaste = prev ?? null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAttachmentCount, maxAttachments]);

  const modules = useMemo(() => {
    if (hideToolbar) {
      return {
        toolbar: false,
        clipboard: { matchVisual: false },
      };
    }

    return {
      toolbar: {
        container: [['bold', 'italic', 'underline'], ['image']],
        handlers: { image: handleToolbarImage },
      },
      clipboard: { matchVisual: false },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideToolbar]);

  return (
    <div>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        placeholder="Please enter your feedback"
        value={value}
        onChange={(v) => onChange(typeof v === 'string' ? v : '')}
        formats={['bold', 'italic', 'underline', 'image']}
        modules={modules}
        readOnly={!!disabled}
        style={{ minHeight, background: '#fff' }}
        className={styles.quillWrapper}
      />
    </div>
  );
}
